import {gql} from '@apollo/client';
import {getDomainGraphqlError, useMutation$} from 'graphqlsupport';
import {useCallback} from 'react';
import {AtRiskReason, AtRiskReasonMap, AtRiskReasonValueToName, AuthorizationBillItem, SubmitAtRiskReasonsInput} from "../../domain";
import {addBusinessDays} from "date-fns";
import { showToast } from 'common';
import {format} from "date-fns-tz";
import {LOCAL_DATE_FORMAT} from "common";

const sanitizeAuthReviewPayload = (authReviewNeeded: any) => {
    if (!authReviewNeeded) return authReviewNeeded;

    return {
        ...authReviewNeeded,
        authReviewFromDate: authReviewNeeded.authReviewFromDate ? format(new Date(authReviewNeeded.authReviewFromDate), LOCAL_DATE_FORMAT) : undefined,
        authReviewToDate: authReviewNeeded.authReviewToDate ? format(new Date(authReviewNeeded.authReviewToDate), LOCAL_DATE_FORMAT) : undefined,
        requestedExtensionDate: authReviewNeeded.requestedExtensionDate ? format(new Date(authReviewNeeded.requestedExtensionDate), LOCAL_DATE_FORMAT) : undefined,
        firstScheduledAssessment: authReviewNeeded.firstScheduledAssessment ? format(new Date(authReviewNeeded.firstScheduledAssessment), LOCAL_DATE_FORMAT) : undefined,
    }
}


type SubmitAtRiskReasonsMutationResult = {
    submitAtRiskReasons: AuthorizationBillItem[],
};
export const SUBMIT_AT_RISK_REASONS_MUTATION = gql`
mutation submitAtRiskReasons($authItemGuids: [UUID], $reasons: [AtRiskResponseReason], $staffingPlan: StaffingPlanInput, $staffingAction: String, $addendumReportGuid: UUID, $treatmentHold: TreatmentHoldInput, $notAnActiveClient: NotAnActiveClientInput, $clientTransferring: ClientTransferringInput, $noContact: NoContactResponseInput, $authReviewNeeded: AuthReviewNeededInput, $updateAuthItemOnly: Boolean) {
  submitAtRiskReasons(authItemGuids: $authItemGuids, reasons: $reasons, staffingPlan: $staffingPlan, staffingAction: $staffingAction, addendumReportGuid: $addendumReportGuid, treatmentHold: $treatmentHold, notAnActiveClient: $notAnActiveClient, clientTransferring: $clientTransferring, noContact: $noContact, authReviewNeeded: $authReviewNeeded, updateAuthItemOnly: $updateAuthItemOnly) {
    guid
    status
    startDate
    endDate
    closedReason
    procedureCode
    modifier
    practitionerRole
    tier
    minimumHoursAuthorized
    maximumHoursAuthorized
    hoursBilled
    directCareHoursRequired
    dateAtRiskPublished
    atRiskResponseDueDate
    atRiskResponseDate
    atRiskResponseReasons
    offeredHoursStatus
    lastModifiedDate
    lastModifiedBy
  }
}`;
export const useSubmitAtRiskReasonsMutation$ = (successCallback: () => void | Promise<void>) => {
    const {execute, isRunning$,} = useMutation$<
        SubmitAtRiskReasonsInput
        , SubmitAtRiskReasonsMutationResult>(SUBMIT_AT_RISK_REASONS_MUTATION);
    const wrappedExecute = useCallback(async (input: SubmitAtRiskReasonsInput) => {
        try {
            const {data} = await execute({
                variables: {
                    ...input,
                    authReviewNeeded: sanitizeAuthReviewPayload(input.authReviewNeeded),
                    updateAuthItemOnly: input.updateAuthItemOnly ?? false,
                },
            });
            if (data) {
                if (input.reasons.includes(AtRiskReasonValueToName.get(AtRiskReason.Staffing) ?? "")) {
                    const staffingAction = input.staffingAction ?? '';
                    const in2BizDay = addBusinessDays(new Date(), 2).toDateString();
                    // Defensive: deferred null staffing submit (no Cancel path calls this anymore)
                    if (staffingAction === 'Staffing Plan' && input.staffingPlan == null) {
                        showToast.warning(`Submission for Staffing Plan is cancelled. Staffing Plan needs to be submitted by ${in2BizDay}`, {duration: 15});
                    } else if ((staffingAction === 'ABA Addendum Report' || staffingAction === 'Change Hours Addendum') && input.addendumReportGuid == null) {
                        showToast.warning(`Submission for ${staffingAction} is cancelled. Addendum needs to be submitted by ${in2BizDay}`, {duration: 15});
                    } else if (staffingAction === 'Staffing Plan' && input.staffingPlan != null) {
                        showToast.success('Staffing Plan request successfully submitted');
                    }
                } else {
                    if (input.reasons.length > 1) {
                        showToast.success('Successfully Submit Reasons');
                    } else {
                        const reason = input.reasons[0];
                        if ((reason === AtRiskReasonValueToName.get(AtRiskReason.Auth_Review_Needed)
                            || reason === AtRiskReasonValueToName.get(AtRiskReason.Treatment_Hold)
                            || reason === AtRiskReasonValueToName.get(AtRiskReason.No_Contact)
                        ) && input.updateAuthItemOnly === false) {
                            showToast.success(`${AtRiskReasonMap.get(reason) ?? reason} request successfully submitted. The Request can be tracked in the “Submitted Requests” section and a request number ID will be available shortly.`);
                        } else {
                            showToast.success('Successfully Submit Reasons');
                        }
                    }
                }
                await successCallback();
            }
        } catch (errors: any) {
            if (getDomainGraphqlError(errors)) {
                showToast.error(getDomainGraphqlError(errors));
            } else {
                showToast.error(`Unable to submit reason by ${errors.message || errors[0].message}`);
            }
        }
    }, [execute, successCallback]);
    return {
        isRunning$,
        submit: wrappedExecute,
    };
}
